<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import {
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectViewport,
} from "reka-ui";

import CaseMenuBar from "@/components/CaseMenuBar.vue";
import { getCasePanelComponent, useAllCasePanels } from "@/hooks/use-case-panel";

import type { PanelType } from "@/hooks/use-case-panel";

defineProps<{ isCaseRoute: boolean }>();

const isMounted = ref(false);
const panelRefs = ref<HTMLElement[]>([]);

const allPanels = useAllCasePanels();

const allPanelsWithComponents = computed(() => {
  return (
    Object.entries(allPanels.value)
      .filter(([, val]) => val.enabled || !isMounted.value)
      // sort panels with undefined orders to the end
      .sort(([, a], [, b]) => (a.order == null || b.order == null ? a.title.localeCompare(b.title) : a.order - b.order))
      .map(([key, val]) => ({ ...val, key, panelComponent: getCasePanelComponent(key as PanelType, false) }))
  );
});

function scrollToPanel(idx: number, dir: "prev" | "next" | "fixed") {
  let toIdx: number;

  if (dir === "prev") {
    if (idx === 0) return;
    toIdx = idx - 1;
  } else if (dir === "next") {
    if (idx === allPanelsWithComponents.value.length) return;
    toIdx = idx + 1;
  } else {
    toIdx = idx;
  }

  const panel = panelRefs.value[toIdx];
  if (panel == null) return;
  panel.scrollIntoView();
  panel.focus();
}

onMounted(() => {
  // Allow panels that run code on mount to run.
  // 1 tick is enough, but will use 2 for good measure
  nextTick(() => nextTick(() => (isMounted.value = true)));
});
</script>
<template>
  <CaseMenuBar />
  <router-view v-if="!isCaseRoute" />
  <div v-else class="tw-h-full tw-snap-y tw-snap-mandatory tw-overflow-y-auto tw-bg-white">
    <div
      v-for="({ key, title, panelComponent }, idx) in allPanelsWithComponents"
      :key="key"
      ref="panelRefs"
      tabindex="0"
      :aria-label="`${title} Panel`"
      class="tw-flex tw-h-full tw-snap-center tw-flex-col tw-overflow-auto"
    >
      <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-0.5">
        <h1 class="tw-m-0 tw-truncate tw-border-0 tw-p-0 tw-text-base tw-font-semibold">{{ title }}</h1>
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-1">
          <button
            v-if="idx > 0"
            type="button"
            class="tw-btn-md-gray tw-min-w-max hover:tw-brightness-90"
            @click="() => scrollToPanel(idx, 'prev')"
          >
            <i aria-hidden="true" class="fa fa-chevron-left" />
            <span :class="{ 'tw-sr-only': idx < allPanelsWithComponents.length - 1 }"> Previous Panel</span>
          </button>
          <button
            v-if="idx < allPanelsWithComponents.length - 1"
            type="button"
            class="tw-btn-md-gray tw-min-w-max hover:tw-brightness-90"
            @click="() => scrollToPanel(idx, 'next')"
          >
            <span :class="{ 'tw-sr-only': idx > 0 }">Next Panel </span>
            <i aria-hidden="true" class="fa fa-chevron-right" />
          </button>
          <SelectRoot :model-value="''" @update:model-value="(idx) => scrollToPanel(+idx, 'fixed')">
            <SelectTrigger aria-label="Jump to Panel" class="tw-btn-md-gray tw-min-w-max">
              Jump to
              <SelectIcon>
                <i aria-hidden="true" class="fas fa-fw fa-chevron-down" />
              </SelectIcon>
            </SelectTrigger>
            <SelectPortal>
              <SelectContent
                avoid-collisions
                position="popper"
                class="tw-z-[100000] tw-overflow-hidden tw-rounded tw-border tw-border-gray-500 tw-bg-white tw-shadow-md"
                :side-offset="4"
                @close-auto-focus="(e) => e.preventDefault()"
              >
                <SelectScrollUpButton />
                <SelectViewport>
                  <template v-for="(panel, jumpToIdx) in allPanelsWithComponents" :key="jumpToIdx">
                    <SelectItem
                      v-if="jumpToIdx !== idx"
                      class="tw-block tw-cursor-pointer tw-px-4 tw-py-1 tw-text-gray-800 hover:tw-bg-purple-100 hover:tw-text-purple-900 focus:tw-bg-gray-100 focus:tw-text-gray-800 focus:tw-outline-none"
                      :value="jumpToIdx.toString()"
                    >
                      {{ panel.title }}
                    </SelectItem>
                  </template>
                </SelectViewport>
                <SelectScrollDownButton />
              </SelectContent>
            </SelectPortal>
          </SelectRoot>
        </div>
      </div>
      <component :is="panelComponent" />
    </div>
  </div>
</template>
